import { useState, useEffect, useCallback } from 'react'
import { checkHandleAvailability, setInitialHandle } from '../../services/api'

function HandleSelectionModal({ proposedHandle, onHandleSet }) {
  const [handle, setHandle] = useState(proposedHandle || '')
  const [validating, setValidating] = useState(false)
  const [availability, setAvailability] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [debounceTimer, setDebounceTimer] = useState(null)

  // Debounced validation
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    if (handle.length < 3) {
      setAvailability(null)
      return
    }

    const timer = setTimeout(async () => {
      setValidating(true)
      try {
        const response = await checkHandleAvailability(handle)
        setAvailability(response.data)
        setError(null)
      } catch (err) {
        setError('Failed to check availability')
        setAvailability(null)
      } finally {
        setValidating(false)
      }
    }, 500)

    setDebounceTimer(timer)
    return () => clearTimeout(timer)
  }, [handle, debounceTimer])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()

    if (!availability?.available) {
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await setInitialHandle(handle)
      if (onHandleSet) {
        onHandleSet(handle)
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to set handle'
      setError(errorMessage)
      setSubmitting(false)
    }
  }, [handle, availability, onHandleSet])

  const canSubmit = availability?.available && !validating && !submitting

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[1000] animate-modal-slide-in">
      <div className="bg-bg-darker border-2 border-border-light rounded-lg p-8 max-w-[450px] w-[90%] shadow-[0_4px_20px_rgba(0,0,0,0.5)]">
        <h2 className="mt-0 text-white text-center text-xl mb-2">Choose Your Handle</h2>
        <p className="text-text-muted text-center mb-6 text-sm">
          This will be your unique identifier in the game. You can only change it once every 30 days.
        </p>

        {proposedHandle && (
          <div className="mb-4 p-3 bg-bg-panel border border-border-light rounded text-sm">
            <p className="text-text-muted mb-1">Suggested (unavailable):</p>
            <p className="text-white font-semibold">@{proposedHandle}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="handle" className="text-text-muted text-xs mb-2 block">
              Enter a unique handle
            </label>
            <input
              id="handle"
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="e.g., shadow_hunter"
              minLength={3}
              maxLength={20}
              disabled={submitting}
              autoFocus
              className="w-full px-3 py-3 border border-border-light rounded-sm bg-bg-panel text-white text-base focus:outline-none focus:border-accent-blue focus-visible:outline-2 focus-visible:outline-accent-blue focus-visible:outline-offset-2"
            />
            <p className="text-text-muted text-xs mt-1">3-20 characters, letters/numbers/underscore only</p>
          </div>

          {/* Validation feedback */}
          {validating && (
            <div className="text-text-muted text-sm">Checking availability...</div>
          )}

          {availability && !availability.available && (
            <div className="text-accent-red text-sm flex items-center gap-2">
              <span>✗</span>
              <span>{availability.reason}</span>
            </div>
          )}

          {availability && availability.available && (
            <div className="text-accent-green text-sm flex items-center gap-2">
              <span>✓</span>
              <span>Handle available</span>
            </div>
          )}

          {error && <div className="text-accent-red text-sm">{error}</div>}

          <button
            type="submit"
            disabled={!canSubmit}
            className="px-3 py-3 bg-accent-blue text-white border-none rounded-sm text-base cursor-pointer transition-colors duration-200 hover:bg-[#0056b3] disabled:bg-[#555] disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-accent-blue focus-visible:outline-offset-2 font-semibold"
          >
            {submitting ? 'Setting Handle...' : 'Confirm Handle'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default HandleSelectionModal
